import { ChangeDetectorRef, Component, DestroyRef, EventEmitter, Input, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

import { PasswordPromptService } from './password-prompt.service';
import { MIN_PASSWORD_LENGTH, PasswordCheck, PasswordFailure, checkPassword, failureKey, passwordFailure } from './password-rules';

/**
 * The "choose a new password" form of the External ID move (ZEY-3140): new + confirm, show/hide, the rules checked as
 * the user types, and the API's typed errors. Shared by the popup and the /change-password page, which put their own
 * buttons next to Save through `[pwAction]` content. When password change isn't available on our side, Save gives way
 * to a Continue button that emits `continued`.
 */
@Component({
  selector: 'app-password-form',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './password-form.component.html',
  styleUrls: ['./password-form.component.scss']
})
export class PasswordFormComponent {
  private readonly service = inject(PasswordPromptService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  /** Translation key of the button shown instead of Save when password change isn't available. */
  @Input() continueKey = 'passwordPromptContinue';
  @Output() readonly saved = new EventEmitter<void>();
  @Output() readonly continued = new EventEmitter<void>();

  readonly minLength = MIN_PASSWORD_LENGTH;

  password = '';
  confirm = '';
  showPassword = false;
  busy = false;
  submitted = false;
  /** Why the last save failed, as the API typed it (ZEY-3140). */
  failure: PasswordFailure | null = null;

  get failureKey(): string | null {
    return this.failure ? failureKey(this.failure) : null;
  }

  /** Not available on our side: show the message and let the user continue. */
  get notAvailable(): boolean {
    return this.failure?.code === 'NotAvailable';
  }

  /** Only the server knows Microsoft's banned list: the "not a common password" line turns red when it refuses. */
  get commonBad(): boolean {
    return this.failure?.code === 'PasswordRejected' && this.failure.rule === 'common' && this.failedPassword === this.password;
  }

  /** The password the last failure was about; editing it clears the red line. */
  private failedPassword: string | null = null;

  get check(): PasswordCheck {
    return checkPassword(this.password, this.confirm);
  }

  toggleShow(): void {
    this.showPassword = !this.showPassword;
  }

  continueAnyway(): void {
    this.failure = null;
    this.continued.emit();
  }

  submit(): void {
    this.submitted = true;
    this.failure = null;
    if (!this.check.valid || this.busy) {
      return;
    }
    this.busy = true;
    this.service
      .setPassword(this.password)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.busy = false;
          this.password = '';
          this.confirm = '';
          this.submitted = false;
          this.saved.emit();
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.busy = false;
          this.failure = passwordFailure(error);
          this.failedPassword = this.password;
          this.cdr.markForCheck();
        }
      });
  }
}
